import { Form, Formik } from 'formik';
import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import { Grid, useEventCallback } from '@mui/material';
import Buttons from 'shell/components/shared/sipo-person-form/new-person/buttons';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import FormNewPerson from 'shell/components/shared/sipo-person-form/new-person/form';
import { getValidationSchema } from 'shell/components/shared/sipo-person-form/new-person-validation';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformNewPerson } from 'practice/utils/transform-new-person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { transformerEditPerson } from 'shell/components/shared/sipo-person-form/transformer-edit-person';
import { SubjectData } from 'shell/models/person';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { AxiosResponse } from 'axios';
import { PersonalDataVM, ResidenceOrigin } from 'shell/view-models/personal-data';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';
import { RestAPIState } from 'shell/hooks/use-http-fetch/reducer/reducer';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';

type Props = {
  onSuccess: (id?: string, state?: { subjectData?: SubjectData }) => void;
  onExit: (id?: string) => void;
  urlGetData?: string;
  urlRegistration?: string;
  urlUpdate?: string;
  residenceOrigin?: RestAPIState<unknown>;
  fiscalCode?: string;
  initialValues?: RegisterOfficeVM & NewPersonVM;
  skipBirthActPartCheck?: boolean;
};

const SipoPersonForm = ({
  onExit,
  onSuccess,
  urlGetData = '',
  urlRegistration = `${__PERSON_API_URL__}/persona/registrazione-non-residenti`,
  urlUpdate = `${__PERSON_API_URL__}/persona/registrazione-non-residenti/modifica`,
  residenceOrigin,
  fiscalCode,
  initialValues: externalInitialValues,
  skipBirthActPartCheck = false,
}: Props): ReactElement => {
  const params = useParams();

  const [active, setActive] = useState<number>(0);

  const [queryParams] = useSearchParams();

  const filterTable = useFilterTable<RegisterOfficeVM & NewPersonVM>();

  const stateRequestApplicant = useLocation().state as PersonalDataVM;

  // id intestatrio se presente fare get perchè siamo in modifica
  const idIntestatario = queryParams.get('idIntestatario') || '';
  const idAnpr = queryParams.get('idAnpr') || '';

  const isEdit = useMemo(() => {
    if ((idIntestatario && idIntestatario !== '0') || (idAnpr && idAnpr !== '0')) {
      return true;
    }

    return false;
  }, [idIntestatario, idAnpr]);

  const [dataInitial, getData] = useHttpFetch<HttpResponse<SubjectData>, NewPersonVM>(``, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: (response) => transformerEditPerson(response, idIntestatario),
  });

  useEffect(() => {
    if (isEdit) {
      getData({
        url: urlGetData,
        method: idIntestatario && idIntestatario !== '0' ? 'GET' : 'POST',
        data:
          idIntestatario && idIntestatario !== '0'
            ? {}
            : {
                criteriRicerca: {
                  idSchedaSoggettoANPR: idAnpr || undefined,
                },
                datiRichiesta: {
                  ricercaNazionale: '1',
                },
              },
      });
    }
  }, [isEdit, idAnpr, idIntestatario]);

  const insertData = useHttpClient<HttpResponse<{ codice: string }>, { codice: string }>(
    urlRegistration,
    {
      options: { method: 'POST' },
      transformer: (response) => {
        return response.data?.payload;
      },
    }
  );

  const updateData = useHttpClient(urlUpdate, {
    options: { method: 'POST' },
  });

  const handleSuccess = useEventCallback(
    (newIdIntestatario?: string, state?: { subjectData?: SubjectData }) => {
      onSuccess(newIdIntestatario, state);
    }
  );

  // eslint-disable-next-line no-console
  console.log('filterTable.values: ', filterTable.values);

  const onSubmit = useEventCallback((values: NewPersonVM) => {
    // const data = isEdit ? transformNewPersonEdit(values) : transformNewPerson(values);
    const data = transformNewPerson(values);

    if (isEdit) {
      (
        updateData({
          data: {
            ...data,
            matricolaSipo: idIntestatario,
            idSchedaSoggettoAnpr: idAnpr || undefined,
            praticaKey: {
              codiceEnte: params.codiceEnte || undefined,
              tipoRegistro: params.tipoRegistro || undefined,
              idAnnoPratica: params.idAnnoPratica
                ? Number.parseInt(params.idAnnoPratica, 10)
                : undefined,
              progressivoPratica: params.progressivoPratica
                ? Number.parseInt(params.progressivoPratica, 10)
                : undefined,
            },
          },
        }) as ResponsePromise<void>
      ).then((response) => {
        const castedResponse = response as unknown as AxiosResponse<
          HttpResponse<{
            key: { codice: number | string };
          }>
        >;
        toast.success('Operazione eseguita con successo');

        filterTable.setValues({
          currentStep: filterTable.values?.currentStep,
          ...(values || {}),
          accountholderData: {
            ...(values || {}),
            citizenship: values.citizenship || undefined,
            identificationDocument: values.identityCard || undefined,
            generality: {
              ...(values.generality || {}),
              municipalityIdentifier: castedResponse?.data?.payload?.key?.codice?.toString() || '',
            },
            mother:
              values.maternity?.generality?.name || values.maternity?.generality?.surname
                ? {
                    name: values.maternity?.generality?.name,
                    surname: values.maternity?.generality?.surname,
                  }
                : undefined,
            father:
              values.paternity?.generality?.name || values.paternity?.generality?.surname
                ? {
                    name: values.paternity?.generality?.name,
                    surname: values.paternity?.generality?.surname,
                  }
                : undefined,
            marriage:
              values.marriage?.generality?.name || values.marriage?.generality?.surname
                ? {
                    name: values.marriage?.generality?.name,
                    surname: values.marriage?.generality?.surname,
                  }
                : undefined,
            civilUnion:
              values.civilUnion?.generality?.name || values.civilUnion?.generality?.surname
                ? {
                    name: values.civilUnion?.generality?.name,
                    surname: values.civilUnion?.generality?.surname,
                  }
                : undefined,
          },
          generality: {
            ...(values.generality || {}),
            istatCode: ISTAT_MILANO,
            municipalityIdentifier: castedResponse?.data?.payload?.key?.codice?.toString() || '',
          },
        });

        handleSuccess(castedResponse?.data?.payload?.key?.codice?.toString());
        return;
      });

      return;
    }

    (
      insertData({ data }) as ResponsePromise<HttpResponse<{ codice: string }>, { codice: string }>
    ).then((response) => {
      toast.success('Operazione eseguita con successo');
      const responseCasted = response as {
        codice: string;
      };

      filterTable.setValues({
        currentStep: filterTable.values?.currentStep,
        ...(values || {}),
        accountholderData: {
          ...(values || {}),
          citizenship: values.citizenship || undefined,
          identificationDocument: values.identityCard || undefined,
          generality: {
            ...(values.generality || {}),
            municipalityIdentifier: responseCasted?.codice,
          },
          mother:
            values.maternity?.generality?.name || values.maternity?.generality?.surname
              ? {
                  name: values.maternity?.generality?.name,
                  surname: values.maternity?.generality?.surname,
                }
              : undefined,
          father:
            values.paternity?.generality?.name || values.paternity?.generality?.surname
              ? {
                  name: values.paternity?.generality?.name,
                  surname: values.paternity?.generality?.surname,
                }
              : undefined,
          marriage:
            values.marriage?.generality?.name || values.marriage?.generality?.surname
              ? {
                  name: values.marriage?.generality?.name,
                  surname: values.marriage?.generality?.surname,
                }
              : undefined,
          civilUnion:
            values.civilUnion?.generality?.name || values.civilUnion?.generality?.surname
              ? {
                  name: values.civilUnion?.generality?.name,
                  surname: values.civilUnion?.generality?.surname,
                }
              : undefined,
        },
        generality: {
          ...(values.generality || {}),
          istatCode: ISTAT_MILANO,
          municipalityIdentifier: responseCasted?.codice,
        },
      });

      handleSuccess(responseCasted?.codice, {
        ...stateRequestApplicant,
        subjectData: {
          generalita: {
            idSchedaSoggettoComune: {
              idSchedaSoggetto: responseCasted?.codice,
            },
          },
        },
      });
    });
  });

  const initialValues: NewPersonVM = useMemo(() => {
    return {
      subjectAttributes: {
        subject: '2',
      },
      generality: {
        fiscalCode: stateRequestApplicant?.fiscalCode || fiscalCode || '',
        surname: stateRequestApplicant?.surname || '',
        name: stateRequestApplicant?.name || '',
        birthDate: stateRequestApplicant?.birthday || null,
        gender: '',
        isForeign: false,
        italian: {
          municipality: null,
        },
        foreign: {
          municipality: null,
          state: null,
        },
        ...(externalInitialValues?.generality || {}),
      },
      birthdayDocument: {
        municipality: null,
        portion: '',
        documentNumber: '',
        volume: '',
        serie: '',
        year: '',
        documentDate: null,
      },
      paternity: {
        generality: {
          fiscalCode: '',
          surname: '',
          name: '',
          birthDate: null,
          gender: '',
          isForeign: false,
          italian: {
            municipality: null,
          },
          foreign: {
            municipality: null,
            state: null,
          },
        },
      },
      maternity: {
        generality: {
          fiscalCode: '',
          surname: '',
          name: '',
          birthDate: null,
          gender: '',
          isForeign: false,
          italian: {
            municipality: null,
          },
          foreign: {
            municipality: null,
            state: null,
          },
        },
      },
      civilStatus: {
        note: '',
        status: '',
      },
      marriage: {
        generality: {
          fiscalCode: '',
          surname: '',
          name: '',
          birthDate: null,
          gender: '',
          isForeign: false,
          italian: {
            municipality: null,
          },
          foreign: {
            municipality: null,
            state: null,
          },
        },
        isForeign: false,
        italian: {
          municipality: null,
        },
        foreign: {
          municipality: null,
          state: null,
        },
        from: null,
        eventDate: null,
        year: '',
        portion: '',
        range: '',
        documentNumber: '',
        regVol: '',
        documentDate: null,
        municipality: null,
      },
      widowhood: {
        isForeign: false,
        italian: {
          municipality: null,
        },
        foreign: {
          municipality: null,
          state: null,
        },
        documentDate: null,
        year: '',
        portion: '',
        range: '',
        documentNumber: '',
        regVol: '',
        eventDate: null,
        municipality: null,
      },
      voidMarriage: {
        type: '',
        eventDate: null,
        validityDate: null,
        authority: '',
        municipality: null,
        portion: '',
        documentNumber: '',
        regVol: '',
        range: '',
        year: '',
        documentDate: null,
      },
      citizenship: [],
      civilUnion: {
        generality: {
          fiscalCode: '',
          surname: '',
          name: '',
          birthDate: null,
          gender: '',
          isForeign: false,
          italian: {
            municipality: null,
          },
          foreign: {
            municipality: null,
            state: null,
          },
        },
        citizenship: null,
        municipality: null,
        eventDate: null,
        isForeign: false,
        italian: {
          municipality: null,
        },
        foreign: {
          municipality: null,
          state: null,
        },
        from: null,
        year: '',
        portion: '',
        range: '',
        documentNumber: '',
        regVol: '',
        documentDate: null,
      },
      voidCivilUnion: {
        number: undefined,
        eventDate: null,
        validityDate: null,
        authority: '',
        municipality: null,
        year: '',
        portion: '',
        range: '',
        documentNumber: '',
        regVol: '',
        documentDate: null,
      },
      drivingLicenseBooklet: { motorVehicles: '', drivingLicense: '' },
      identityCard: {
        number: '',
        isConsulateRelease: false,
        consulateCode: '',
        municipality: null,
        location: '',
        releaseDate: null,
        expirationDate: null,
        cancellationDate: null,
      },
      residencyPermit: {
        number: '',
        type: '',
        releaseDate: null,
        expirationDate: null,
        policeForceRelease: '',
        passportNumber: '',
      },
      ...((dataInitial?.data as NewPersonVM) || {}),
      residenceOrigin:
        filterTable.values?.currentStep === 1 &&
        isEdit &&
        filterTable.values?.fromList &&
        residenceOrigin
          ? (residenceOrigin?.data as ResidenceOrigin) || {}
          : undefined,
    };
  }, [
    dataInitial.data,
    filterTable.values?.currentStep,
    residenceOrigin?.data,
    externalInitialValues,
  ]);

  return (
    <Fragment>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={getValidationSchema(skipBirthActPartCheck)}
        enableReinitialize
      >
        <Form>
          <Grid container spacing={1}>
            <Grid item xs={12} md={2}>
              <Buttons
                handleExit={() => onExit()}
                handleStep={setActive}
                activeStep={active}
                isEdit={isEdit}
                withProvenienza={
                  filterTable.values?.currentStep === 1 && isEdit && filterTable.values?.fromList
                }
              />
            </Grid>
            <Grid item xs={12} md={10}>
              <FormNewPerson step={active} isEdit={isEdit} typeRegister={params.tipoRegistro} />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Fragment>
  );
};

SipoPersonForm.displayName = 'Sipo Person form component';

export default SipoPersonForm;
